import React, { useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createTheme, useMediaQuery } from '@mui/material';
import { Box, Paper, Stack, TextField, Button, Typography, CircularProgress, Divider, Checkbox, FormControlLabel, Card, CardContent, IconButton, Grid, InputAdornment, CssBaseline, Autocomplete, Link, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import axios from 'axios';
import { fetchCatalog, sendOtp, verifyOtp, submitOrder, fetchCountries, type Device, type Country, type PricingRole } from './api/ordering';
import { PHONE_CODES, type PhoneCode } from './data/phoneCodes';
import { clearOrderingToken } from './api/client';
import MobileHeader from './components/MobileHeader';
import MobileCartPage from './components/MobileCartPage';
import MobileShippingPage from './components/MobileShippingPage';


type BrandConfig = { brand_name: string; logo_url: string|null; favicon_url: string|null; primary_color: string; background_color: string; primary_text_color?: string|null; background_text_color?: string|null; contact_email?: string|null; contact_phone?: string|null };

export type CartItem = { device_id: number; name: string; unit_price_minor: number; currency: string; quantity: number };

function currency(amount: number, currency: string) {
  const value = Number(amount || 0);
  return `${currency} ${value.toFixed(2)}`;
}

export default function App() {
  const isMobile = useMediaQuery('(max-width:900px)'); // Same breakpoint as MUI md
  const [brand, setBrand] = useState<BrandConfig | null>(null);
  const [loadingBrand, setLoadingBrand] = useState(true);
  const [catalog, setCatalog] = useState<{ devices: Device[] }>({ devices: [] });
  const [pricingRole, setPricingRole] = useState<PricingRole>('MERCHANT');
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [step, setStep] = useState(0);
  const SESSION_KEY = 'orderingSession';

  const [email, setEmail] = useState('');
  // Split phone into dial code + local number (like onboarding)
  const [dialCode, setDialCode] = useState<string>('+44');
  const [localPhone, setLocalPhone] = useState<string>('');
  const [channel, setChannel] = useState<'email'|'phone'>('phone');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [resendSec, setResendSec] = useState(0);

  const [cart, setCart] = useState<CartItem[]>([]);
  const [expandedDesc, setExpandedDesc] = useState<Record<number, boolean>>({});
  const [company, setCompany] = useState('');
  const [notes, setNotes] = useState('');
  const [street, setStreet] = useState('');
  const [street2, setStreet2] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('');
  const [countries, setCountries] = useState<Country[]>([]);
  const [accept, setAccept] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{order_uid: string; total_minor: number; currency: string} | null>(null);
  // const [selectedCategory, setSelectedCategory] = useState<string>('All'); // Future use
  // const [orderType, setOrderType] = useState<'DINE_IN'|'TAKE_AWAY'|'DELIVERY'>('DINE_IN'); // Future use
  // const [paymentMethod, setPaymentMethod] = useState<'CASH'|'CARD'|'QR'>('CARD'); // Future use
  // const [promo, setPromo] = useState(''); // Future use
  // const [discountMinor, setDiscountMinor] = useState(0); // Future use
  // const taxRate = 0; // no tax - Future use
  const [detailProduct, setDetailProduct] = useState<Device | null>(null);
  const [detailQty, setDetailQty] = useState<number>(1);
  const [showMobileCart, setShowMobileCart] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const host = window.location.hostname;
        const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
        const res = await axios.get(`${baseURL}/onboarding/brand`, { params: { host } });
        const cfg = res.data as BrandConfig;
        setBrand(cfg);
        // Favicon: prefer brand favicon_url; fallback to public /favicon.png
        {
          let link: HTMLLinkElement | null = document.querySelector("link[rel='icon']");
          if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
          link.type = 'image/png';
          link.href = cfg.favicon_url || '/favicon.png';
        }
        document.title = cfg.brand_name ? `Order Devices – ${cfg.brand_name}` : 'Order Devices';
      } finally {
        setLoadingBrand(false);
      }
    })();
  }, []);

  // Add HeyGen streaming embed widget
  useEffect(() => {
    try {
      // Avoid duplicate mounts (HMR / re-renders)
      const existing = document.getElementById('heygen-streaming-embed');
      if (existing) {
        existing.remove();
      }
      const host = 'https://labs.heygen.com';
      const url = host + '/guest/streaming-embed?share=eyJxdWFsaXR5IjoiaGlnaCIsImF2YXRhck5hbWUiOiJHcmFoYW1fQmxhY2tfU2hpcnRfcHVibGlj%0D%0AIiwicHJldmlld0ltZyI6Imh0dHBzOi8vZmlsZXMyLmhleWdlbi5haS9hdmF0YXIvdjMvMWJkMDE2%0D%0AMmNlOTg1NDE1MDkyZWQxZjAwM2FhOTA2NDZfNTUzNTAvcHJldmlld190YXJnZXQud2VicCIsIm5l%0D%0AZWRSZW1vdmVCYWNrZ3JvdW5kIjp0cnVlLCJrbm93bGVkZ2VCYXNlSWQiOiI1NTY5NjE4YjljMjE0%0D%0AYjZjYWVkNGNkZGMzMmU2Y2MyMCIsInVzZXJuYW1lIjoiZWI1YjY2YTMyYWE1NDUwNzkwMDVjZDIy%0D%0ANGJmZWU3N2MifQ%3D%3D&inIFrame=1';
      const clientWidth = document.body.clientWidth;
      const wrapDiv = document.createElement('div');
      wrapDiv.id = 'heygen-streaming-embed';
      const container = document.createElement('div');
      container.id = 'heygen-streaming-container';
      const stylesheet = document.createElement('style');
      const expandCss = clientWidth < 540
        ? 'height: 266px; width: 96%; left: 50%; transform: translateX(-50%);'
        : 'height: 366px; width: calc(366px * 16 / 9);';
      stylesheet.innerHTML = `
  #heygen-streaming-embed {
    z-index: 9999;
    position: fixed;
    left: 83px;
    bottom: 16px;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: 0;
    box-shadow: 0px 8px 24px 0px rgba(0, 0, 0, 0.12);
    transition: all linear 0.1s;
    overflow: hidden;

    opacity: 0;
    visibility: hidden;
  }
  #heygen-streaming-embed.show {
    opacity: 1;
    visibility: visible;
  }
  #heygen-streaming-embed.expand {
    ${expandCss}
    border: 0;
    border-radius: 8px;
  }
  #heygen-streaming-container {
    width: 100%;
    height: 100%;
  }
  #heygen-streaming-container iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
      `;
      const iframe = document.createElement('iframe');
      iframe.setAttribute('title', 'Streaming Embed');
      iframe.setAttribute('role', 'dialog');
      iframe.setAttribute('allow', 'microphone');
      iframe.src = url;
      // Scale down iframe content to fit 52x52 bubble
      const virtualSize = 320; // assumed base size of embed UI
      const scale = 52 / virtualSize;
      iframe.style.width = `${virtualSize}px`;
      iframe.style.height = `${virtualSize}px`;
      iframe.style.transformOrigin = 'top left';
      iframe.style.transform = `scale(${scale})`;
      // Make whole bubble clickable when collapsed
      const clickCatcher = document.createElement('div');
      clickCatcher.setAttribute('aria-label', 'Open chat');
      clickCatcher.style.position = 'absolute';
      clickCatcher.style.inset = '0';
      clickCatcher.style.zIndex = '2';
      clickCatcher.style.background = 'transparent';
      clickCatcher.style.cursor = 'pointer';
      let initial = false;
      const expandNow = () => {
        wrapDiv.classList.toggle('expand', true);
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.transformOrigin = 'top left';
        iframe.style.transform = 'none';
        iframe.style.pointerEvents = 'auto';
        clickCatcher.style.display = 'none';
        wrapDiv.style.cursor = 'default';
      };
      const collapseNow = () => {
        wrapDiv.classList.toggle('expand', false);
        iframe.style.width = `${virtualSize}px`;
        iframe.style.height = `${virtualSize}px`;
        iframe.style.transformOrigin = 'top left';
        iframe.style.transform = `scale(${scale})`;
        iframe.style.pointerEvents = 'none';
        clickCatcher.style.display = 'block';
        wrapDiv.style.cursor = 'pointer';
      };
      clickCatcher.addEventListener('click', expandNow);
      const messageHandler = (e: MessageEvent) => {
        if (e.origin === host && e.data && (e.data as any).type === 'streaming-embed') {
          const action = (e.data as any).action;
          if (action === 'init') {
            initial = true;
            wrapDiv.classList.toggle('show', initial);
          } else if (action === 'show') {
            expandNow();
          } else if (action === 'hide') {
            collapseNow();
          }
        }
      };
      window.addEventListener('message', messageHandler);
      container.appendChild(iframe);
      wrapDiv.appendChild(stylesheet);
      wrapDiv.appendChild(clickCatcher);
      wrapDiv.appendChild(container);
      document.body.appendChild(wrapDiv);
      // start collapsed and ensure overlay receives clicks
      try { collapseNow(); } catch (_) {}
      return () => {
        window.removeEventListener('message', messageHandler);
        try { clickCatcher.removeEventListener('click', expandNow); } catch (_) {}
        const node = document.getElementById('heygen-streaming-embed');
        if (node && node.parentNode) node.parentNode.removeChild(node);
      };
    } catch (_) {
      // ignore embed errors
    }
  }, []);

  // Countdown for resend
  useEffect(() => {
    if (resendSec <= 0) return;
    const id = setInterval(() => setResendSec((s) => Math.max(s - 1, 0)), 1000);
    return () => clearInterval(id);
  }, [resendSec]);

  // Restore session on load (step/cart/address)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (raw) {
        const s = JSON.parse(raw || '{}') as any;
        if (!s.expiresAt || Date.now() < Number(s.expiresAt)) {
          if (typeof s.step === 'number') setStep(s.step);
          if (Array.isArray(s.cart)) setCart(s.cart);
          if (typeof s.company === 'string') setCompany(s.company);
          if (typeof s.notes === 'string') setNotes(s.notes);
          if (typeof s.street === 'string') setStreet(s.street);
          if (typeof s.street2 === 'string') setStreet2(s.street2);
          if (typeof s.city === 'string') setCity(s.city);
          if (typeof s.province === 'string') setProvince(s.province);
          if (typeof s.zip === 'string') setZip(s.zip);
          if (typeof s.country === 'string') setCountry(s.country);
        } else {
          // expired – clear
          localStorage.removeItem(SESSION_KEY);
          clearOrderingToken();
          setStep(0);
        }
      }
    } catch (_) {}
  }, []);

  // Global logout listener (triggered on 401/403)
  useEffect(() => {
    function handleLogout() {
      try { localStorage.removeItem(SESSION_KEY); } catch (_) {}
      try { clearOrderingToken(); } catch (_) {}
      setCart([]);
      setExpandedDesc({});
      setCompany('');
      setNotes('');
      setStreet('');
      setStreet2('');
      setCity('');
      setProvince('');
      setZip('');
      setCountry('');
      setEmail('');
      setLocalPhone('');
      setOtp('');
      setStep(0);
    }
    window.addEventListener('ordering:logout', handleLogout);
    return () => window.removeEventListener('ordering:logout', handleLogout);
  }, []);

  // In-tab auto-expiry check (every 30s)
  useEffect(() => {
    const id = setInterval(() => {
      try {
        const raw = localStorage.getItem(SESSION_KEY);
        if (!raw) return;
        const s = JSON.parse(raw || '{}') as any;
        if (s.expiresAt && Date.now() >= Number(s.expiresAt)) {
          window.dispatchEvent(new Event('ordering:logout'));
        }
      } catch (_) {}
    }, 30000);
    return () => clearInterval(id);
  }, []);

  // Persist lightweight session
  useEffect(() => {
    try {
      const payload = {
        step,
        cart,
        company,
        notes,
        street,
        street2,
        city,
        province,
        zip,
        country,
        expiresAt: Date.now() + 30 * 60 * 1000, // 30 minutes
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(payload));
    } catch (_) {}
  }, [step, cart, company, notes, street, street2, city, province, zip, country]);
  const loadCatalog = React.useCallback(async () => {
    setLoadingCatalog(true);
    try {
      const c = await fetchCatalog();
      setCatalog(c);
      if (c.pricing_role) setPricingRole(c.pricing_role);
    } catch (error) {
      console.error('Failed to load catalog:', error);
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  // Load catalog only after OTP (step >= 1) so the request includes the ordering JWT.
  // A mount-time fetch without a token races verify and can overwrite distributor pricing with merchant prices.
  useEffect(() => {
    if (step === 1) {
      void loadCatalog();
    }
  }, [step, loadCatalog]);
  useEffect(() => { (async () => { try { const cs = await fetchCountries(); setCountries(cs); } catch (_) {} })(); }, []);
  // autodetect dial code
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('https://ipapi.co/json/');
        const data = await resp.json();
        const cc = (data && data.country_code) ? String(data.country_code).toUpperCase() : '';
        if (cc) {
          const match = PHONE_CODES.find(pc => pc.iso2 === cc);
          if (match) setDialCode(match.dial.replace(/\s/g,''));
          setCountry(cc); // default country based on IP
        }
      } catch (_) {}
    })();
  }, []);

  const theme = useMemo(() => {
    // Use fixed colors to match the reference dashboard style
    return createTheme({
      palette: {
        primary: { main: '#e6ac30', contrastText: '#ffffff' }, // updated primary
        success: { main: '#22c55e' },
        warning: { main: '#f59e0b' }, // amber
        error: { main: '#ef4444' },   // red
        background: { default: '#f7f9fb', paper: '#ffffff' },
        text: { primary: '#111827', secondary: '#6b7280' },
        divider: 'rgba(0,0,0,0.08)',
      },
      shape: { borderRadius: 12 },
    });
  }, []);

  function getUnitPrice(device: Device, qty: number) {
    const tiers = (device.DevicePriceTiers || []).slice().sort((a,b)=>b.min_qty-a.min_qty);
    let unit = device.base_price_minor;
    for (const t of tiers) { if (qty >= t.min_qty) { unit = t.unit_price_minor; break; } }
    return unit;
  }

  const deviceTotal = cart.reduce((s, it) => s + (it.unit_price_minor * it.quantity), 0);
  const totalMinor = Math.max(deviceTotal, 0);

  const fullPhone = (dialCode || '') + (localPhone || '');
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = (v: string) => v.trim().length > 0 && emailRe.test(v.trim());
  const canSendOtp = isValidEmail(email) && localPhone.length >= 5;
  const canVerify = canSendOtp && otp.length >= 4;
  const canSubmit = cart.length > 0;

  function addToCart(device: Device, qty: number) {
    if (qty < 1) return;
    setCart((prev) => {
      const existing = prev.find(it => it.device_id === device.id);
      const newQty = (existing ? existing.quantity : 0) + qty;
      const unit = getUnitPrice(device, newQty);
      if (existing) {
        return prev.map(it => it.device_id === device.id ? { ...it, quantity: newQty, unit_price_minor: unit } : it);
      }
      return [...prev, { device_id: device.id, name: device.name, unit_price_minor: unit, currency: device.currency, quantity: newQty }];
    });
  }
  function updateCartQty(deviceId: number, qty: number) {
    setCart((prev) => prev.map(it => {
      if (it.device_id !== deviceId) return it;
      const d = catalog.devices.find(x => x.id === deviceId);
      const unit = d ? getUnitPrice(d, qty) : it.unit_price_minor;
      return { ...it, quantity: Math.max(qty, 0), unit_price_minor: unit };
    }).filter(it => it.quantity > 0));
  }
  function removeFromCart(deviceId: number) {
    setCart((prev) => prev.filter(it => it.device_id !== deviceId));
  }

  const logoUrl = (brand?.logo_url && String(brand.logo_url)) || '/brand-logo.svg';

  if (loadingBrand) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default, color: theme.palette.text.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Stack spacing={2} alignItems="center"><CircularProgress /><Typography>Loading…</Typography></Stack>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <style>
        {`
          @media (max-width: 900px) {
            .css-wvnmvq {
              margin-top: -48px !important;
            }
          }
        `}
      </style>
      <Box sx={{ minHeight: '100vh', height: '100vh', overflow: 'hidden', bgcolor: theme.palette.background.default, color: theme.palette.text.primary }}>
        <Box component="a" href="https://wa.me/447539270119" target="_blank" rel="noopener" aria-label="WhatsApp Chat"
             sx={{ position: 'fixed', left: 16, bottom: 16, width: 52, height: 52, bgcolor: '#25D366', color: '#ffffff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 3, textDecoration: 'none', zIndex: 9998, '&:hover': { bgcolor: '#1ebe5d' } }}>
          <WhatsAppIcon />
        </Box>
        {step === 0 ? (
          <Box sx={{ height: 'calc(100vh - 16px)', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 16, p: 2, overflow: 'hidden' }}>
            {/* Left: improved branding sidebar - only render on desktop */}
            {isMobile ? null : (
              <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f7f9fb', borderRadius: 3, p: 3, pb: 16, height: '100%', position: 'relative' }}>
              <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center' }}>
                <Box component="img" src={logoUrl} alt="logo" sx={{ height: 56, objectFit: 'contain' }} />
                <Box component="img" src="/brand-hero.png" alt="brand" sx={{ width: '100%', borderRadius: 2, objectFit: 'cover', maxHeight: 314 }} />
              </Stack>
              <Box sx={{ flex: 1 }} />
              <Divider sx={{ my: 2 }} />
              <Stack spacing={0.5}>
                <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Contact</Typography>
                <Typography variant="body2">Email: {brand?.contact_email || 'support@example.com'}</Typography>
                <Typography variant="body2">Phone: {brand?.contact_phone || '+1 555 0100'}</Typography>
                <Typography variant="caption" color="text.secondary">Mon–Fri 09:00–18:00</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                If you have any questions, you can contact us directly on WhatsApp or ask our AI assistant, alex, below.
              </Typography>

            </Box>
            )}
            {/* Right: OTP form with country code */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Paper sx={{ p: 3, borderRadius: 3, width: '100%', maxWidth: 720 }}>
                <Stack spacing={3}>
                  <Stack spacing={1} alignItems={{ xs: 'center', md: 'flex-start' }}>
                    <Box component="img" src={logoUrl} alt="logo" sx={{ height: 40, display: { xs: 'block', md: 'none' } }} />
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>Verify your contact</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enter both email and phone. Pricing uses whichever matches your distributor or merchant record — OTP is only for verification and can be sent to either email or phone.
                    </Typography>
                  </Stack>
                  <TextField label="Email" fullWidth value={email} onChange={e=>setEmail(e.target.value)} />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Autocomplete
                      options={PHONE_CODES}
                      autoHighlight
                      fullWidth
                      value={(PHONE_CODES.find(pc => pc.dial.replace(/\s/g,'') === dialCode) as PhoneCode) || null}
                      onChange={(_, val) => setDialCode(val ? val.dial.replace(/\s/g,'') : '+')}
                      getOptionLabel={(o) => `${o.name} (${o.dial})`}
                      renderInput={(params) => <TextField {...params} label="Dial code" />}
                      sx={{ minWidth: 220, maxWidth: 320 }}
                    />
                    <TextField
                      label="Phone number"
                      fullWidth
                      value={localPhone}
                      onChange={e=>setLocalPhone(e.target.value.replace(/[^\d]/g,''))}
                      InputProps={{ startAdornment: <InputAdornment position="start">{dialCode}</InputAdornment> }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button variant={channel==='phone'?'contained':'outlined'} onClick={()=>setChannel('phone')}>Send OTP to phone</Button>
                    <Button variant={channel==='email'?'contained':'outlined'} onClick={()=>setChannel('email')}>Send OTP to email</Button>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button disabled={!canSendOtp || sendLoading || resendSec>0} onClick={async()=>{
                      setSendLoading(true);
                      try { 
                        await sendOtp(channel, email.trim(), fullPhone); 
                        setOtpSent(true);
                        setResendSec(120);
                      } finally { setSendLoading(false); }
                    }}>{resendSec>0 ? `Resend in ${String(Math.floor(resendSec/60)).padStart(2,'0')}:${String(resendSec%60).padStart(2,'0')}` : (otpSent ? 'Resend OTP' : 'Send OTP')}</Button>
                    <TextField label="Enter OTP" fullWidth value={otp} onChange={e=>setOtp(e.target.value)} />
                    <Button variant="contained" disabled={!canVerify || verifying} onClick={async()=>{
                      setVerifying(true);
                      try {
                        const res = await verifyOtp(channel, email.trim(), fullPhone, otp);
                        if (res.pricing_role) setPricingRole(res.pricing_role);
                        setStep(1);
                      } finally { setVerifying(false); }
                    }}>Verify & Continue</Button>
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          </Box>
        ) : null}

        {step === 1 && !showMobileCart ? (
          <Box
            sx={{
              height: 'calc(100vh - 16px)',
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: '260px 1fr 360px',   // restore cart width
                lg: '260px 1fr 360px',
              },
              gap: 8,                      // minimize space between columns
              p: 1,                        // reduce outer padding to reclaim width
              overflow: 'hidden',
            }}
          >
            {/* Mobile Header - shows on mobile only */}
            <MobileHeader
              cart={cart}
              onCartClick={() => setShowMobileCart(true)}
              step={step}
              logoUrl={logoUrl}
            />

            {/* Left: Branding sidebar - only render on desktop */}
            {isMobile ? null : (
              <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f7f9fb', borderRadius: 3, p: 3, pb: 16, height: '100%', flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
              <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center' }}>
                <Box component="img" src={logoUrl} alt="logo" sx={{ height: 56, objectFit: 'contain' }} />
                <Box component="img" src="/brand-hero.png" alt="brand" sx={{ width: '100%', borderRadius: 2, objectFit: 'cover', maxHeight: 314 }} />
              </Stack>
              <Box sx={{ flex: 1 }} />
              <Divider sx={{ my: 2 }} />
              <Stack spacing={0.5}>
                <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Contact</Typography>
                <Typography variant="body2">Email: {brand?.contact_email || 'support@example.com'}</Typography>
                <Typography variant="body2">Phone: {brand?.contact_phone || '+1 555 0100'}</Typography>
                <Typography variant="caption" color="text.secondary">Mon–Fri 09:00–18:00</Typography>
              </Stack>

            </Box>
            )}

            {/* Middle content */}
            <Box
              sx={{
                overflowY: 'auto',
                overflowX: 'hidden',
                minHeight: 0,
                // hide scrollbar but keep scroll functionality
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                // Ensure mobile scrolling works properly - account for header, chat icons, and checkout button area
                height: { xs: 'calc(100vh - 46px)', md: 'auto' }, // Reduced from 52px to 46px for much tighter spacing
                // Add bottom padding to ensure last product is fully visible above fixed elements
                pb: { xs: 14, md: 2 }, // Increased padding on mobile to ensure visibility above left-side chat icons
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1.1rem' }}>Product List</Typography>
              </Stack>

              {loadingCatalog ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                  <Stack spacing={2} alignItems="center">
                    <CircularProgress size={40} sx={{ color: '#e6ac30' }} />
                    <Typography variant="body1" color="text.secondary">
                      Loading products...
                    </Typography>
                  </Stack>
                </Box>
              ) : (
              <Grid container spacing={1.5} columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                {catalog.devices.slice().reverse().map(d => {
                  const unit = getUnitPrice(d, 1);
                  const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
                  let imageUrl: string|undefined = undefined;
                  try {
                    const origin = new URL(apiBase).origin;
                    if (d.image_key) imageUrl = `${origin}/api/file?key=${encodeURIComponent(d.image_key)}`;
                  } catch (_) {}
                  const hasImage = !!imageUrl;
                  const desc = d.description || '';
                  const limit = 80;
                  const isExpanded = !!expandedDesc[d.id];
                  const needsClamp = desc.length > limit;
                  const displayDesc = isExpanded ? desc : (needsClamp ? (desc.slice(0, limit).trim() + '…') : desc);
                  return (
                    <Grid item xs={12} sm={6} md={3} lg={3} key={d.id}>
                      <Card
                        onClick={()=>{ setDetailProduct(d); setDetailQty(1); }}
                        sx={{ borderRadius: 3, boxShadow: 3, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
                        role="button"
                      >
                        <Box sx={{ position: 'relative', m: 1.5, mb: 0, borderRadius: 2, overflow: 'hidden', height: 140, bgcolor: '#f3f5f7', backgroundImage: hasImage ? `url(${imageUrl})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {!hasImage && (
                            <Typography variant="h5" sx={{ color: '#94a3b8', fontWeight: 800 }}>{String(d.name || '?').charAt(0).toUpperCase()}</Typography>
                          )}
                        </Box>
                        <CardContent sx={{ px: 1.5, pt: 1, pb: 0, display: 'flex', flexDirection: 'column', gap: 0.25, flexGrow: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{d.name}</Typography>
                          {desc && (
                            <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {displayDesc}
                            </Typography>
                          )}
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body1" sx={{ fontWeight: 900, color: '#e6ac30' }}>{currency(unit, d.currency)}</Typography>
                          </Box>
                        </CardContent>
                        <Box sx={{ px: 1.5, pb: 1.5, pt: 1 }}>
                          <Button
                            fullWidth
                            onClick={(e)=>{ e.stopPropagation(); addToCart(d, 1); }}
                            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: 'rgba(230,172,48,0.15)', color: '#e6ac30', borderRadius: 2, height: 38, '&:hover': { bgcolor: 'rgba(230,172,48,0.25)' } }}
                          >
                            Add to Cart
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              )}
            </Box>

            {/* Right cart panel */}
            <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#ffffff', borderRadius: 2, p: 2, height: '100%', minHeight: 0, overflow: 'hidden' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Order items</Typography>
                <Button size="small" variant="text">Clear all items</Button>
              </Stack>
              <Stack spacing={1} sx={{ mb: 2, flex: 1, overflow: 'auto', minHeight: 0 }}>
                {cart.length === 0 && <Typography variant="body2" color="text.secondary">No items yet.</Typography>}
                {cart.map(it => (
                  <Paper key={it.device_id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{it.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{currency(it.unit_price_minor, it.currency)} each</Typography>
                      </Box>
                      <IconButton size="small" onClick={()=>updateCartQty(it.device_id, Math.max(it.quantity-1,0))}><AddIcon fontSize="small" sx={{ transform: 'rotate(45deg)' }}/></IconButton>
                      <TextField
                        size="small"
                        type="number"
                        value={it.quantity}
                        onChange={(e)=>updateCartQty(it.device_id, Math.max(Number(e.target.value),0))}
                        sx={{ width: 96 }}
                        inputProps={{ min: 0, style: { textAlign: 'center' } }}
                      />
                      <IconButton size="small" onClick={()=>updateCartQty(it.device_id, it.quantity+1)}><AddIcon fontSize="small"/></IconButton>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Total amount</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{currency(totalMinor, (cart[0]?.currency || 'GBP'))}</Typography>
                </Stack>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Device Order Notes</Typography>
                  <Typography variant="caption" color="text.secondary">
                    If you'd like to order a quantity different from what is listed, Device order section please add it here. Also add your special requests here.
                  </Typography>
                  <TextField
                    label="Notes"
                    value={notes}
                    onChange={e=>setNotes(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                  />
                </Stack>
              </Paper>
              <Button variant="contained" size="large" disabled={cart.length===0} onClick={()=>setStep(2)}>Checkout</Button>
            </Box>
          </Box>
        ) : null}

        {/* Mobile Cart Page */}
        {step === 1 && showMobileCart ? (
          <MobileCartPage
            cart={cart}
            onBack={() => setShowMobileCart(false)}
            onUpdateQty={updateCartQty}
            onRemoveItem={removeFromCart}
            onCheckout={() => setStep(2)}
            totalMinor={totalMinor}
            currency={(cart[0]?.currency || 'GBP')}
            notes={notes}
            onNotesChange={setNotes}
          />
        ) : null}

        {/* Desktop Shipping Page */}
        {step === 2 && !isMobile ? (
          <Box sx={{ height: 'calc(100vh - 16px)', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '260px 1fr' }, gap: 16, p: 2, overflow: 'hidden' }}>
            {/* Left: Branding sidebar - only render on desktop */}
            {isMobile ? null : (
              <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f7f9fb', borderRadius: 3, p: 3, pb: 16, height: '100%', position: 'relative' }}>
              <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center' }}>
                <Box component="img" src={logoUrl} alt="logo" sx={{ height: 56, objectFit: 'contain' }} />
                <Box component="img" src="/brand-hero.png" alt="brand" sx={{ width: '100%', borderRadius: 2, objectFit: 'cover', maxHeight: 314 }} />
              </Stack>
              <Box sx={{ flex: 1 }} />
              <Divider sx={{ my: 2 }} />
              <Stack spacing={0.5}>
                <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Contact</Typography>
                <Typography variant="body2">Email: {brand?.contact_email || 'support@example.com'}</Typography>
                <Typography variant="body2">Phone: {brand?.contact_phone || '+1 555 0100'}</Typography>
                <Typography variant="caption" color="text.secondary">Mon–Fri 09:00–18:00</Typography>
              </Stack>

            </Box>
            )}
            {/* Right: Shipping / order details */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
              <Paper sx={{ p: 3, borderRadius: 3, width: '100%', maxWidth: 800 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">Company & Shipping</Typography>
                  <TextField label="Company Name" value={company} onChange={e=>setCompany(e.target.value)} />
                  <TextField label="Notes" value={notes} onChange={e=>setNotes(e.target.value)} multiline rows={2} />
                  <TextField label="Street" value={street} onChange={e=>setStreet(e.target.value)} />
                  <TextField label="Address line 2" value={street2} onChange={e=>setStreet2(e.target.value)} />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="City" value={city} onChange={e=>setCity(e.target.value)} />
                    <TextField label="Province" value={province} onChange={e=>setProvince(e.target.value)} />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="ZIP" value={zip} onChange={e=>setZip(e.target.value)} />
                    <Autocomplete
                      options={countries}
                      autoHighlight
                      fullWidth
                      value={countries.find(c => c.id === country) || null}
                      onChange={(_e, val) => setCountry(val ? String(val.id) : '')}
                      getOptionLabel={(o) => o.name}
                      renderInput={(params) => <TextField {...params} label="Country" size="small" />}
                    />
                  </Stack>
                  <FormControlLabel
                    control={<Checkbox checked={accept} onChange={e=>setAccept(e.target.checked)} />}
                    label={
                      <Typography variant="body2">
                        I accept the{' '}
                        <Link
                          href="#"
                          onMouseDown={(e)=>e.preventDefault()}
                          onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setTermsOpen(true); }}
                        >
                          terms and conditions
                        </Link>
                      </Typography>
                    }
                  />
                  <Stack direction="row" spacing={2}>
                    <Button onClick={()=>setStep(1)}>Back</Button>
                    <Button variant="contained" disabled={!canSubmit || submitting} onClick={async()=>{
                      setSubmitting(true);
                      try {
                        const items = cart.map(it => ({ device_id: it.device_id, quantity: it.quantity }));
                        const payload = { brand_host: window.location.hostname, email, phone: fullPhone, company_name: company, notes, items, street, street2, city, province, zip, country, currency: (cart[0]?.currency || 'GBP'), signed_at: new Date().toISOString() };
                        const res = await submitOrder(payload);
                        setResult(res);
                        setStep(3);
                        // On successful checkout, expire session and token
                        try { clearOrderingToken(); } catch (_) {}
                        try { localStorage.removeItem(SESSION_KEY); } catch (_) {}
                      } finally { setSubmitting(false); }
                    }}>Place order</Button>
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          </Box>
        ) : null}

        {/* Mobile Shipping Page */}
        {step === 2 && isMobile ? (
          <MobileShippingPage
            onBack={() => setStep(1)}
            company={company}
            setCompany={setCompany}
            notes={notes}
            setNotes={setNotes}
            street={street}
            setStreet={setStreet}
            street2={street2}
            setStreet2={setStreet2}
            city={city}
            setCity={setCity}
            province={province}
            setProvince={setProvince}
            zip={zip}
            setZip={setZip}
            country={country}
            setCountry={setCountry}
            countries={countries}
            accept={accept}
            setAccept={setAccept}
            termsOpen={termsOpen}
            setTermsOpen={setTermsOpen}
            onSubmit={async () => {
              setSubmitting(true);
              try {
                const items = cart.map(it => ({ device_id: it.device_id, quantity: it.quantity }));
                const payload = { brand_host: window.location.hostname, email, phone: fullPhone, company_name: company, notes, items, street, street2, city, province, zip, country, currency: (cart[0]?.currency || 'GBP'), signed_at: new Date().toISOString() };
                const res = await submitOrder(payload);
                setResult(res);
                setStep(3);
                // On successful checkout, expire session and token
                try { clearOrderingToken(); } catch (_) {}
                try { localStorage.removeItem(SESSION_KEY); } catch (_) {}
              } finally { setSubmitting(false); }
            }}
            submitting={submitting}
          />
        ) : null}

        {step === 3 && result ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
            <Paper sx={{ p: 3, borderRadius: 3, width: '100%', maxWidth: 640 }}>
              <Stack spacing={2} alignItems="center">
                <Typography variant="h6">Thank you!</Typography>
                <Typography>Your order ID is <strong>{result.order_uid}</strong>.</Typography>
                <Typography>Total: {currency(result.total_minor, result.currency)}</Typography>
                <Button onClick={()=>{ 
                  try { clearOrderingToken(); } catch (_) {}
                  try { localStorage.removeItem(SESSION_KEY); } catch (_) {}
                  setCart([]); 
                  setExpandedDesc({}); 
                  setStep(0); 
                }}>Back to shop</Button>
              </Stack>
            </Paper>
          </Box>
        ) : null}

        {/* Product Detail Dialog */}
        <Dialog open={!!detailProduct} onClose={()=>setDetailProduct(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{detailProduct?.name || ''}</DialogTitle>
          <DialogContent dividers>
            {detailProduct && (
              <Stack spacing={2}>
                <Box sx={{ width: '100%', height: 260, borderRadius: 2, overflow: 'hidden', bgcolor: '#f3f5f7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {detailProduct.image_key ? (() => {
                    try {
                      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
                      const origin = new URL(apiBase).origin;
                      const url = `${origin}/api/file?key=${encodeURIComponent(detailProduct.image_key)}`;
                      return <Box component="img" sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} src={url} alt={detailProduct.name} />;
                    } catch (_e) {
                      return <Box sx={{ p: 4, textAlign: 'center', color: '#94a3b8' }}><Typography variant="h5">{String(detailProduct.name || '?').charAt(0).toUpperCase()}</Typography></Box>;
                    }
                  })() : (
                    <Box sx={{ p: 4, textAlign: 'center', color: '#94a3b8' }}>
                      <Typography variant="h5">{String(detailProduct.name || '?').charAt(0).toUpperCase()}</Typography>
                    </Box>
                  )}
                </Box>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {detailProduct.description || 'No description provided.'}
                </Typography>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Pricing</Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">Base price: {currency(detailProduct.base_price_minor, detailProduct.currency)}</Typography>
                    {(detailProduct.DevicePriceTiers || []).length > 0 && (
                      <Box component="ul" sx={{ pl: 2, m: 0 }}>
                        {(detailProduct.DevicePriceTiers || []).slice().sort((a,b)=>a.min_qty - b.min_qty).map(t => (
                          <li key={t.id}>
                            <Typography variant="body2">Buy {t.min_qty}+ @ {currency(t.unit_price_minor, t.currency)}</Typography>
                          </li>
                        ))}
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ alignItems: 'center' }}>
            <TextField
              size="small"
              type="number"
              label="Qty"
              value={detailQty}
              onChange={(e)=>setDetailQty(Math.max(1, Number(e.target.value)||1))}
              sx={{ width: 120 }}
              inputProps={{ min: 1 }}
            />
            <Box sx={{ flex: 1 }} />
            <Button onClick={()=>setDetailProduct(null)}>Close</Button>
            <Button
              variant="contained"
              onClick={()=>{ if (detailProduct) { addToCart(detailProduct, Math.max(1, Number(detailQty)||1)); setDetailProduct(null); } }}
            >
              Add to Cart
            </Button>
          </DialogActions>
        </Dialog>

        {/* Terms & Conditions Modal */}
        <Dialog open={termsOpen} onClose={()=>setTermsOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Terms & Conditions</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Payment Terms: as pr. invoice.</Typography>
              <Typography variant="body2">
                To ensure you benefit from our most favorable payment terms, we require all invoices to be settled on time. If a payment is overdue by more than five working days, a late fee of £150 will be applied. An additional charge of £50 will be added for each subsequent five-working-day period that the invoice remains unpaid. Prompt payment is essential to maintain your preferential terms.
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Monthly Support & Licensing Fee:</Typography>
              <Typography variant="body2">
                £7 pr. device pr. month for SIM + TMS, charged monthly with direct debit.
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Delivery Terms:</Typography>
              <Typography variant="body2">
                Goods will be delivered to the shipping address provided by the Buyer in the order form.
              </Typography>
              <Typography variant="body2">
                Delivery will occur only after full payment of the invoice has been received.
              </Typography>
              <Typography variant="body2">
                Risk of loss or damage transfers to the Buyer upon delivery.
              </Typography>
              <Typography variant="body2">
                Estimated delivery times are provided as a guide and are not guaranteed. The Seller is not liable for any delays in delivery.
              </Typography>
              <Typography variant="body2">
                Delivery of Goods will be made to the address specified by the Buyer in the order.
              </Typography>
              <Typography variant="body2">
                Delivery times are estimates and are not guaranteed. The Seller shall not be liable for any delays in delivery.
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Warranty</Typography>
              <Typography variant="body2">
                The Seller warrants that all hardware Goods will be free from defects in materials and workmanship for a period of 12 months from the date of delivery.
              </Typography>
              <Typography variant="body2">
                This warranty does not cover damage caused by misuse, neglect, accident, or unauthorised modification.
              </Typography>
              <Typography variant="body2">
                The Seller's sole liability under this warranty is limited to repairing or replacing defective Goods.
              </Typography>
              <Typography variant="body2">Thank you for Choosing SynthiaX Ltd.</Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>setTermsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}